"use client";

import { useTranslation } from "react-i18next";
import FileUploadIcon from "@/_old/assets/icons/file-upload.svg?react";
import type { ChatSession } from "../hooks/useChatSession";
import { MessagesList } from "./message-list";
import { EmailValidationPanel } from "./email-validation-panel";
import { AgentHubPicker } from "./agent-hub-picker";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Corpo do chat compartilhado entre a janela flutuante e o Centro de ações
 * com IA (PED-12262, Fase 2): lista de mensagens, welcome com hub de agentes
 * e painel de validação de e-mails importados de planilha.
 */
export function ChatSessionMessages({ session }: { session: ChatSession }) {
  const [translate] = useTranslation(["core"]);
  const {
    visibleMessages,
    isWelcome,
    isLoading,
    endRef,
    startRef,
    shouldAutoScrollRef,
    userInitials,
    userInfo,
    extractionResult,
    extractionConfirmed,
    messagesCountBeforeEmailPanel,
    handleSuggestionClick,
    handleHubShortcutClick,
    handleEmailConfirm,
    formHandlers,
    setExtractionResult,
    setExtractionConfirmed,
  } = session;

  return (
    <MessagesList
      messages={isWelcome ? [] : visibleMessages}
      isLoading={isLoading}
      endRef={endRef}
      startRef={startRef}
      userInitials={userInitials}
      onSuggestionClick={handleSuggestionClick}
      formHandlers={formHandlers}
      shouldAutoScrollRef={shouldAutoScrollRef}
      messagesCountBeforeEmailPanel={messagesCountBeforeEmailPanel}
      welcomeHubPicker={
        isWelcome && !isLoading ? (
          <AgentHubPicker onShortcutClick={handleHubShortcutClick} />
        ) : null
      }
      emailValidationPanel={
        extractionResult ? (
          <div className="space-y-4 pt-2">
            {/* Card do arquivo anexado */}
            <div className="ml-auto flex max-w-[98%] flex-row-reverse">
              <div className="flex min-w-0 max-w-[380px] items-center justify-center gap-3 rounded-[16px] bg-aiBlue-100 p-4">
                <FileUploadIcon className="h-8 w-10 shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-bold text-[#333]"
                    title={extractionResult.fileName ?? "planilha.xlsx"}
                  >
                    {extractionResult.fileName ?? "planilha.xlsx"}
                  </p>
                  <p className="text-sm font-normal text-[#5C5C5C]">
                    {extractionResult.fileSize != null
                      ? formatFileSize(extractionResult.fileSize)
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Resumo da importação (Lia) */}
            <div className="flex max-w-[98%] gap-2 px-4">
              <div className="flex flex-col gap-1">
                <div className="rounded-lg px-0 py-3 text-base text-darkgray-600">
                  <p className="mb-4">
                    {translate("chatbot-import-summary-intro")}
                  </p>
                  <p className="mb-2">
                    ✅{" "}
                    {translate("chatbot-import-summary-ready", {
                      count: extractionResult.validEmails.length,
                    })}
                  </p>
                  <p className="mb-4">
                    ⚠️{" "}
                    {translate("chatbot-import-summary-need-adjustment", {
                      count: extractionResult.invalidEmails.length,
                    })}
                  </p>
                  <p className="mb-4">
                    {translate("chatbot-import-summary-corrections")}{" "}
                    <strong>
                      {translate("chatbot-import-reason-duplicate")}
                    </strong>
                    {translate("chatbot-import-summary-corrections-and")}
                    <strong>{translate("chatbot-import-reason-format")}</strong>
                    .
                  </p>
                  <p>
                    {translate("chatbot-import-summary-time-prefix")}
                    <strong>
                      {translate("chatbot-import-summary-time-bold")}
                    </strong>
                    {translate("chatbot-import-summary-time-suffix")}
                  </p>
                </div>
              </div>
            </div>

            <EmailValidationPanel
              validEmails={extractionResult.validEmails}
              invalidEmails={extractionResult.invalidEmails}
              hasEmailColumn={extractionResult.hasEmailColumn}
              totalRows={extractionResult.totalRows}
              allowedDomains={userInfo?.league?.domains ?? []}
              confirmed={extractionConfirmed}
              onConfirm={handleEmailConfirm}
              onCancel={() => {
                setExtractionResult(null);
                setExtractionConfirmed(false);
              }}
            />
          </div>
        ) : null
      }
    />
  );
}
